import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import supabase from '../supabase/supabase';

const GuardianContext = createContext();

export const useGuardian = () => {
  const context = useContext(GuardianContext);
  if (!context) {
    throw new Error('useGuardian must be used within GuardianProvider');
  }
  return context;
};

export const GuardianProvider = ({ children }) => {
  const { user } = useAuth();
  const [guardians, setGuardians] = useState([]);
  const [athleteAccounts, setAthleteAccounts] = useState([]);
  const [currentAthleteId, setCurrentAthleteId] = useState(null);
  const [isGuardian, setIsGuardian] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadGuardianData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadGuardianData = async () => {
    try {
      setLoading(true);

      // Check if user is a guardian of any accounts
      const { data: guardianRelationships, error: guardianError } = await supabase
        .from('guardians')
        .select(`
          *,
          athlete:profiles!guardians_athlete_id_fkey(
            user_id,
            first_name,
            last_name,
            email,
            sport,
            graduation_year
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (guardianError) throw guardianError;

      if (guardianRelationships && guardianRelationships.length > 0) {
        setIsGuardian(true);
        setAthleteAccounts(guardianRelationships);
        
        // Set first athlete as current if none selected
        if (!currentAthleteId) {
          setCurrentAthleteId(guardianRelationships[0].athlete_id);
        }
      }

      // Load guardians for this user (if they are an athlete)
      const { data: userGuardians, error: userGuardiansError } = await supabase
        .from('guardians')
        .select('*')
        .eq('athlete_id', user.id)
        .eq('status', 'active');

      if (userGuardiansError) throw userGuardiansError;

      setGuardians(userGuardians || []);
    } catch (error) {
      console.error('Error loading guardian data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActiveUserId = () => {
    // If user is a guardian and has selected an athlete account, return that
    if (isGuardian && currentAthleteId) {
      return currentAthleteId;
    }
    // Otherwise return the logged-in user's ID
    return user?.id;
  };

  const switchAccount = (athleteId) => {
    setCurrentAthleteId(athleteId);
  };

  const inviteGuardian = async (guardianData) => {
    try {
      // Generate invitation token
      const token = Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      // Set expiration to 7 days from now
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const { data, error } = await supabase
        .from('guardian_invitations')
        .insert({
          athlete_id: user.id,
          guardian_email: guardianData.email,
          guardian_name: guardianData.name,
          relationship: guardianData.relationship,
          invitation_token: token,
          expires_at: expiresAt.toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      // In a real app, send email here
      // For now, return the invitation link
      const inviteLink = `${window.location.origin}/#/accept-invitation/${token}`;
      
      return { success: true, inviteLink, data };
    } catch (error) {
      console.error('Error inviting guardian:', error);
      throw error;
    }
  };

  const acceptInvitation = async (token, guardianUserId) => {
    try {
      // Get invitation details
      const { data: invitation, error: inviteError } = await supabase
        .from('guardian_invitations')
        .select('*')
        .eq('invitation_token', token)
        .eq('status', 'pending')
        .single();

      if (inviteError || !invitation) {
        throw new Error('Invalid or expired invitation');
      }

      // Check if invitation is expired
      if (new Date(invitation.expires_at) < new Date()) {
        throw new Error('Invitation has expired');
      }

      // Create guardian relationship
      const { error: guardianError } = await supabase
        .from('guardians')
        .insert({
          user_id: guardianUserId,
          athlete_id: invitation.athlete_id,
          guardian_email: invitation.guardian_email,
          guardian_name: invitation.guardian_name,
          relationship: invitation.relationship,
          accepted_at: new Date().toISOString()
        });

      if (guardianError) throw guardianError;

      // Update invitation status
      const { error: updateError } = await supabase
        .from('guardian_invitations')
        .update({ status: 'accepted' })
        .eq('id', invitation.id);

      if (updateError) throw updateError;

      // Reload guardian data
      await loadGuardianData();

      return { success: true };
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw error;
    }
  };

  const removeGuardian = async (guardianId) => {
    try {
      const { error } = await supabase
        .from('guardians')
        .delete()
        .eq('id', guardianId)
        .eq('athlete_id', user.id);

      if (error) throw error;

      await loadGuardianData();
      return { success: true };
    } catch (error) {
      console.error('Error removing guardian:', error);
      throw error;
    }
  };

  const getPendingInvitations = async () => {
    try {
      const { data, error } = await supabase
        .from('guardian_invitations')
        .select('*')
        .eq('athlete_id', user.id)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString());

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error getting pending invitations:', error);
      return [];
    }
  };

  const cancelInvitation = async (invitationId) => {
    try {
      const { error } = await supabase
        .from('guardian_invitations')
        .delete()
        .eq('id', invitationId)
        .eq('athlete_id', user.id);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error canceling invitation:', error);
      throw error;
    }
  };

  return (
    <GuardianContext.Provider
      value={{
        guardians,
        athleteAccounts,
        currentAthleteId,
        isGuardian,
        loading,
        getActiveUserId,
        switchAccount,
        inviteGuardian,
        acceptInvitation,
        removeGuardian,
        getPendingInvitations,
        cancelInvitation,
        loadGuardianData
      }}
    >
      {children}
    </GuardianContext.Provider>
  );
};